import React, { FC, Fragment, useState, useEffect } from "react";
import { Dialog, Transition, Tab } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import { postLoginData, postSignUpData } from "services/HomeService";
import { AxiosResponse } from "axios";
import Cookies from "js-cookie";

export interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultTab?: "login" | "signup";
}

interface LoginFormData {
  email: string;
  password: string;
}

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const AuthModal: FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultTab = "login",
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const merchantname = Cookies.get("merchantname");

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: "", password: "" },
  });

  const signupForm = useForm<SignupFormData>({
    defaultValues: {
      confirmPassword: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
    },
  });

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const clearPreviousAuth = () => {
    Cookies.remove("CustomerInfo");
    Cookies.remove("userDataaInfo");
    Cookies.remove("addedPayments");
    Cookies.remove("authToken");
    Cookies.remove("BillingAddress");
    Cookies.remove("checkTime");
    Cookies.remove("fromTime");
    Cookies.remove("toTime");
    Cookies.remove("user");
    Cookies.remove("SignupData");
    Cookies.remove("customerAdded");
    Cookies.remove("paymentId");
    Cookies.remove("loginTime");
    Cookies.remove("orderFee");
    Cookies.remove("orderFeeLabel");
    Cookies.remove("phone");
    sessionStorage.removeItem("onlineOrderSettingsLogo");
  };

  const handleLoginSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setErrorMessage("");
    clearPreviousAuth();

    try {
      const response: AxiosResponse<any> = await postLoginData({
        email: data.email,
        password: data.password,
        schemaName: merchantname,
      });

      const customerInfo = response.data["Customer Info "];
      const basilAuthNet = response.data["Basil AuthNet Profile Info "];

      Cookies.set("CustomerInfo", JSON.stringify(customerInfo));
      const authNetProfile =
        basilAuthNet ?? response.data["AuthNet Profile Info "] ?? null;
      Cookies.set("userDataaInfo", JSON.stringify(authNetProfile ?? {}));

      const paymentprofiles =
        authNetProfile?.paymentProfiles ??
        (response.data as any)["paymentProfiles"] ??
        [];
      Cookies.set("addedPayments", JSON.stringify(paymentprofiles));

      const profileId =
        authNetProfile?.customerProfileId ?? customerInfo?.customerId;
      Cookies.set("user", profileId != null ? String(profileId) : "");

      if (customerInfo?.token) {
        Cookies.set("authToken", customerInfo.token, { secure: true, sameSite: "strict" });
      }
      Cookies.set("loginTime", new Date().getTime().toString());

      onClose();
      onSuccess?.();
      window.location.reload();
    } catch (error: any) {
      const errData = error?.response?.data;
      setErrorMessage(
        errData?.error?.message ??
          errData?.message ??
          "Login failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    setErrorMessage("");
    clearPreviousAuth();

    try {
      const signupData = {
        basicInfo: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          password: data.password,
        },
      };

      const response: AxiosResponse<any> = await postSignUpData(
        signupData,
        "False"
      );

      const customerDTO = response.data?.customerDTO;
      if (customerDTO) {
        Cookies.set("CustomerInfo", JSON.stringify(customerDTO));
        Cookies.set("SignupData", JSON.stringify(response.data));
        Cookies.set("customerAdded", JSON.stringify(customerDTO));

        if (customerDTO.token) {
          Cookies.set("authToken", customerDTO.token, { secure: true, sameSite: "strict" });
        }

        const profileId =
          response.data?.NewBasilAuthNetProfile?.customerProfileId ??
          response.data?.NewAuthNetProfile?.customerProfileId ??
          customerDTO.customerId;
        Cookies.set("user", profileId != null ? String(profileId) : "");
        Cookies.set("loginTime", new Date().getTime().toString());
      }

      onClose();
      onSuccess?.();
      window.location.reload();
    } catch (error: any) {
      const errData = error?.response?.data;
      setErrorMessage(
        errData?.error?.message ??
          errData?.message ??
          "Signup failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
    return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-end sm:items-center sm:justify-center p-0 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-full sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-2">
                <Dialog.Title className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  Welcome
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="mx-5 mt-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400">
                  {errorMessage}
                </div>
              )}

              {/* Tabs */}
              <Tab.Group
                defaultIndex={defaultTab === "signup" ? 1 : 0}
              >
                <Tab.List className="flex mx-5 mt-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <Tab
                    className={({ selected }) =>
                      `flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors ${
                        selected
                          ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                      }`
                    }
                  >
                    Sign In
                  </Tab>
                  <Tab
                    className={({ selected }) =>
                      `flex-1 py-2.5 text-sm font-semibold rounded-md transition-colors ${
                        selected
                          ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                      }`
                    }
                  >
                    Create Account
                  </Tab>
                </Tab.List>

                <Tab.Panels className="px-5 pb-6">
                  {/* Login Panel */}
                  <Tab.Panel className="pt-5">
                    <form
                      className="space-y-4"
                      onSubmit={loginForm.handleSubmit(handleLoginSubmit)}
                    >
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Email
                        </label>
                        <Controller
                          name="email"
                          control={loginForm.control}
                          rules={{ required: "Email is required" }}
                          render={({ field }) => (
                            <input
                              type="email"
                              placeholder="you@example.com"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent placeholder:text-slate-400"
                              {...field}
                            />
                          )}
                        />
                        {loginForm.formState.errors.email && (
                          <p className="text-xs text-red-500 mt-1">
                            {loginForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Password
                        </label>
                        <Controller
                          name="password"
                          control={loginForm.control}
                          rules={{ required: "Password is required" }}
                          render={({ field }) => (
                            <input
                              type="password"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
                              {...field}
                            />
                          )}
                        />
                        {loginForm.formState.errors.password && (
                          <p className="text-xs text-red-500 mt-1">
                            {loginForm.formState.errors.password.message}
                          </p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-3 bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 rounded-xl font-semibold text-sm hover:bg-primary-600 dark:hover:bg-primary-500 transition-colors disabled:opacity-60"
                      >
                        {isSubmitting ? "Signing in..." : "Sign In"}
                      </button>
                    </form>
                  </Tab.Panel>

                  {/* Signup Panel */}
                  <Tab.Panel className="pt-5">
                    <form
                      className="space-y-4"
                      onSubmit={signupForm.handleSubmit(handleSignupSubmit)}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            First Name
                          </label>
                          <Controller
                            name="firstName"
                            control={signupForm.control}
                            rules={{ required: "Required" }}
                            render={({ field }) => (
                              <input
                                type="text"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
                                {...field}
                              />
                            )}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Last Name
                          </label>
                          <Controller
                            name="lastName"
                            control={signupForm.control}
                            rules={{ required: "Required" }}
                            render={({ field }) => (
                              <input
                                type="text"
                                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
                                {...field}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Email
                        </label>
                        <Controller
                          name="email"
                          control={signupForm.control}
                          rules={{ required: "Email is required" }}
                          render={({ field }) => (
                            <input
                              type="email"
                              placeholder="you@example.com"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent placeholder:text-slate-400"
                              {...field}
                            />
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Phone
                        </label>
                        <Controller
                          name="phone"
                          control={signupForm.control}
                          rules={{ required: "Phone is required" }}
                          render={({ field }) => (
                            <input
                              type="tel"
                              placeholder="(555) 123-4567"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent placeholder:text-slate-400"
                              {...field}
                              onChange={(e) =>
                                field.onChange(formatPhone(e.target.value))
                              }
                            />
                          )}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Password
                        </label>
                        <Controller
                          name="password"
                          control={signupForm.control}
                          rules={{
                            required: "Password is required",
                            minLength: {
                              value: 8,
                              message: "At least 8 characters",
                            },
                            validate: {
                              hasUppercase: (v) =>
                                /[A-Z]/.test(v) || "Must contain an uppercase letter",
                              hasLowercase: (v) =>
                                /[a-z]/.test(v) || "Must contain a lowercase letter",
                              hasNumber: (v) =>
                                /\d/.test(v) || "Must contain a number",
                            },
                          }}
                          render={({ field }) => (
                            <input
                              type="password"
                              placeholder="Min 8 chars, upper, lower, number"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent placeholder:text-slate-400"
                              {...field}
                            />
                          )}
                        />
                        {signupForm.formState.errors.password && (
                          <p className="text-xs text-red-500 mt-1">
                            {signupForm.formState.errors.password.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Confirm Password
                        </label>
                        <Controller
                          name="confirmPassword"
                          control={signupForm.control}
                          rules={{
                            required: "Please confirm your password",
                            validate: (v) =>
                              v === signupForm.watch("password") || "Passwords do not match",
                          }}
                          render={({ field }) => (
                            <input
                              type="password"
                              placeholder="Re-enter password"
                              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent placeholder:text-slate-400"
                              {...field}
                            />
                          )}
                        />
                        {signupForm.formState.errors.confirmPassword && (
                          <p className="text-xs text-red-500 mt-1">
                            {signupForm.formState.errors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-3 bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 rounded-xl font-semibold text-sm hover:bg-primary-600 dark:hover:bg-primary-500 transition-colors disabled:opacity-60"
                      >
                        {isSubmitting ? "Creating account..." : "Create Account"}
                      </button>
                    </form>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>

              {/* Guest checkout option */}
              <div className="px-5 pb-5 text-center">
                <button
                  onClick={onClose}
                  className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
                >
                  Continue as Guest
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AuthModal;
